import { useState } from "react";
import { useTranslations } from "next-intl";
import { PDeleteConfirmation } from "../../PDeleteConfirmation/PDeleteConfirmation";
import { CheckboxWithValidation } from "../../Inputs/CheckboxWithValidation";
import { OverwritePreview } from "@/lib/utils/workflow-overwrite-preview";

/**
 * Soft, non-blocking preview shown when an admin saves a guest whose fields
 * will be changed by an enabled workflow (UPDATE_GUEST action). Lists the
 * resulting changes — conflicts with the admin's own input highlighted — and
 * lets them save anyway. Reuses {@link PDeleteConfirmation}'s children slot,
 * exactly like the required-fields warning.
 *
 * Offers a "don't show this again" opt-out; `onConfirm` receives whether it was
 * ticked so the caller can persist it per-event (see workflowOverwriteWarnPref).
 */
export const WorkflowOverwriteDialog: React.FC<{
  preview: OverwritePreview;
  loading: boolean;
  onClose: () => void;
  onConfirm: (dontShowAgain: boolean) => void;
}> = ({ preview, loading, onClose, onConfirm }) => {
  const t = useTranslations("popup-create-guest");
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const renderValue = (v: string) =>
    v === "" ? <em className="font-quaternary">{t("workflowPreviewEmpty")}</em> : <strong>{v}</strong>;

  return (
    <PDeleteConfirmation
      title={t("workflowPreviewTitle")}
      message={t("workflowPreviewMessage")}
      primaryButtonText={t("workflowPreviewConfirm")}
      isLoading={loading}
      onCloseAction={onClose}
      onConfirmAction={async () => onConfirm(dontShowAgain)}
    >
      <ul className="flex flex-col gap-1.5 body-3-regular font-secondary w-full">
        {preview.entries.map((e) => (
          <li
            key={e.field}
            className={`flex flex-col rounded-lg px-3 py-2 ${
              e.isConflict ? "surface-error" : "surface-tertiary"
            }`}
          >
            <span className="font-primary body-3-medium">{e.label}</span>
            <span className="flex flex-wrap items-center gap-1">
              {e.ambiguous ? (
                <span className="font-error">{t("workflowPreviewAmbiguous")}</span>
              ) : (
                <>
                  {e.fromValueDisplay !== "" && (
                    <>
                      <span className="line-through font-quaternary">
                        {e.fromValueDisplay}
                      </span>
                      <span aria-hidden>→</span>
                    </>
                  )}
                  {renderValue(e.toValueDisplay)}
                </>
              )}
            </span>
            {e.workflowNames.length > 0 && (
              <span className="body-4-regular font-quaternary">
                {t("workflowPreviewVia", { names: e.workflowNames.join(", ") })}
              </span>
            )}
          </li>
        ))}
      </ul>
      {preview.skippedNonTriggering && (
        <p className="mt-2 body-4-regular font-quaternary">
          {t("workflowPreviewScopeNote")}
        </p>
      )}
      <div className="mt-3">
        <CheckboxWithValidation
          isChecked={dontShowAgain}
          onCheckChangeAction={setDontShowAgain}
          size={18}
          testId="workflow-overwrite-dont-show-again"
        >
          <span className="body-3-regular font-secondary">
            {t("workflowPreviewDontShowAgain")}
          </span>
        </CheckboxWithValidation>
      </div>
    </PDeleteConfirmation>
  );
};
